import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Layout, Menu, Tooltip } from "antd";
import styled from "styled-components";
import Icon, {
  RightOutlined,
  LeftOutlined,
  HomeFilled,
  TrophyFilled,
  GiftFilled,
  SmileFilled,
  SnippetsFilled,
  QuestionCircleFilled,
  ReadOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import Books from "../../assets/images/custom_svgs/Books.jsx";
import Skillsoft from "../../assets/images/custom_svgs/Skillsoft.jsx";
import Admission from "../../assets/images/custom_svgs/Admission.jsx";
import IMyLearningJourney from "../../assets/images/custom_svgs/IMyLearningJourney.jsx";
import ITeamMonitoring from "../../assets/images/custom_svgs/ITeamMonitoring.jsx";

// for translate
import { useTranslation } from "react-i18next";

// color config
import {
  ColorPrimary,
  backgroundDisabledArrow,
} from "../../config/constant/color/index";

// import styles
import styles from "./styles";

const { Sider } = Layout;

import CustomeLink from "../CustomeLink";
import useActionsSidebar from "./hooks/useActions";
import { ListSidebar } from "../../config/constant/sidebar/index.js";
import { getDataPercipio, getIsBlendedLearning } from "../../utils.js";

const FloatingHover = styled.div`
  right: -15px;
  margin-top: 20px;
  background: #ffffff;
  border-radius: 50px;
  width: 30px;
  height: 30px;
  position: absolute;
  border: 1px solid ${backgroundDisabledArrow};
  box-shadow: 1px 3px 11px -6px #000000;
  display: flex;
  justify-content: center;
  z-index: 5;
  cursor: pointer;
  &:hover {
    color: ${ColorPrimary};
  }
`;

const Sidebar = ({ mainState, userData }) => {
  const reRenderMenu = useSelector((state) => state.localization.reRenderMenu);
  const remoteProperty = useSelector((state) => state.remote.remoteProperty);

  const { t, i18n } = useTranslation();

  const [collapsedMenu, setCollapsedMenu] = useState(true);
  const [resultFilterMenu, setResultFilterMenu] = useState([]);
  const [showContent, setShowContent] = useState(!collapsedMenu);

  const { loadingSidebar, listSidebar } = useActionsSidebar();

  // Percipio
  const percData = getDataPercipio();
  const loginUrl = percData.url_login || "#";

  useEffect(() => {
    filterMenu(userData);
  }, [userData, reRenderMenu]);

  const MenuItem = [
    {
      key: "home",
      icon: <HomeFilled />,
      name: "btn-home-menu",
      alias: "Home",
      label: (
        <div>
          <CustomeLink to="/home" mainState={mainState}>
            {t("main.sidebar.home")}
          </CustomeLink>
        </div>
      ),
    },
    {
      key: "learningJourney",
      icon: <Icon component={IMyLearningJourney} />,
      name: "btn-my-learning-journey-menu",
      alias: "My Learning Journey",
      label: (
        <div>
          <CustomeLink to="/my-learning-journey" mainState={mainState}>
            {t("main.sidebar.my_learning_journey")}
          </CustomeLink>
        </div>
      ),
    },
    {
      key: "contentLibrary",
      icon: <Icon component={Books} />,
      name: "btn-content-library-menu",
      alias: "Content Library",
      label: (
        <div>
          <CustomeLink to="/content-library" mainState={mainState}>
            {t("main.sidebar.content_library")}
          </CustomeLink>
        </div>
      ),
    },
    {
      key: "divider-1",
      type: "divider",
    },
    {
      key: "leaderboards",
      icon: <TrophyFilled />,
      name: "btn-leaderboards-menu",
      alias: "Leaderboard",
      label: (
        <div>
          <CustomeLink to="/leaderboards" mainState={mainState}>
            {t("main.sidebar.leaderboards")}
          </CustomeLink>
        </div>
      ),
    },
    {
      key: "rewards",
      icon: <GiftFilled />,
      name: "btn-rewards-menu",
      alias: "Rewards",
      label: (
        <div>
          <CustomeLink to="/rewards" mainState={mainState}>
            {t("main.sidebar.rewards")}
          </CustomeLink>
        </div>
      ),
    },
    {
      type: "divider",
    },
    {
      key: "teamMonitoring",
      icon: <Icon component={ITeamMonitoring} />,
      name: "btn-team-monitoring-menu",
      alias: "Team Monitoring",
      label: (
        <div>
          <CustomeLink to="/team-monitoring" mainState={mainState}>
            {t("main.sidebar.team_monitoring")}
          </CustomeLink>
        </div>
      ),
    },
    {
      key: "reviews",
      icon: <SnippetsFilled />,
      name: "btn-reviews-menu",
      alias: "Reviews",
      label: (
        <div>
          <CustomeLink to="/reviews" mainState={mainState}>
            {t("main.sidebar.reviews")}
          </CustomeLink>
        </div>
      ),
    },
    {
      key: "divider-2",
      type: "divider",
    },
    {
      key: "skillsoft",
      icon: <Icon component={Skillsoft} />,
      name: "btn-skillsoft-menu",
      alias: "Skillsoft",
      label: (
        <div>
          <a
            href={loginUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Skillsoft Percipio
          </a>
        </div>
      ),
    },
    {
      key: "divider-3",
      type: "divider",
    },
    {
      key:"scan",
      icon:  <Icon component={Admission} />,
      name:"btn-scanner-menu",
      label: (
        <div>
          <CustomeLink to="/scan" mainState={mainState}>
            {t("main.sidebar.scanner")}
          </CustomeLink>
        </div>
      )
    },
    {
      key: "divider-4",
      type: "divider",
    },
    {
      key: "help",
      icon: <QuestionCircleFilled />,
      name: "btn-help-menu",
      alias: "Help",
      label: (
        <div>
          <CustomeLink to="/help/faq" mainState={mainState}>
            {t("main.sidebar.help_center")}
          </CustomeLink>
        </div>
      ),
    }
  ];

  const filterMenu = (data) => {
    // Prepare whitelist keys from ListSidebar for whitelabeling validation
    const listSidebarKey = ListSidebar.map((item) => item.key.toLowerCase());

    // Get Percipio configuration from localStorage
    const percData = getDataPercipio();
    const isShowPercipio = percData.is_show_percipio == "true";

    const isBlendedLearning = getIsBlendedLearning() == "true";


    const filter = MenuItem.filter(function (obj) {
      if(isBlendedLearning){

        if (data.is_supervisor === 1) {
          if(isShowPercipio){
            return obj;
          } else {
            return (
              obj.key !== "skillsoft" &&
              obj.key !== "divider-3"
            );
          }
        } else {
          if(isShowPercipio){
            return (
              obj.key !== "teamMonitoring" &&
              obj.key !== "reviews" &&
              obj.key !== "divider-2"
            );
          }else{
            return (
              obj.key !== "teamMonitoring" &&
              obj.key !== "reviews" &&
              obj.key !== "divider-2" &&
              obj.key !== "skillsoft" &&
              obj.key !== "divider-3"
            );
          }
        }

      } else {

        if (data.is_supervisor === 1) {
          if(isShowPercipio){
            return (
              obj.key !== "scan" &&
              obj.key !== "divider-4"
            )
          } else {
            return (
              obj.key !== "skillsoft" &&
              obj.key !== "divider-3" &&
              obj.key !== "scan" &&
              obj.key !== "divider-4"
            );
          }
        } else {
          if(isShowPercipio){
            return (
              obj.key !== "teamMonitoring" &&
              obj.key !== "reviews" &&
              obj.key !== "divider-2" &&
              obj.key !== "scan" &&
              obj.key !== "divider-4"
            );
          }else{
            return (
              obj.key !== "teamMonitoring" &&
              obj.key !== "reviews" &&
              obj.key !== "divider-2" &&
              obj.key !== "skillsoft" &&
              obj.key !== "divider-3" &&
              obj.key !== "scan" &&
              obj.key !== "divider-4"
            );
          }
        }
      }
    });

    // const filter = MenuItem.filter(function (obj) {
    //   if (data.is_supervisor === 1) {
    //     if(isShowPercipio){
    //       return obj;
    //     } else {
    //       return (
    //       obj.key !== "skillsoft" &&
    //       obj.key !== "divider-3"
    //     );
    //     }
    //   } else {
    //     if(isShowPercipio){
    //       return (
    //         obj.key !== "teamMonitoring" &&
    //         obj.key !== "reviews" &&
    //         obj.key !== "divider-2"
    //       );
    //     }else{
    //       return (
    //         obj.key !== "teamMonitoring" &&
    //         obj.key !== "reviews" &&
    //         obj.key !== "divider-2" &&
    //         obj.key !== "skillsoft" &&
    //         obj.key !== "divider-3"
    //       );
    //   }
    //   }
    // });

    setResultFilterMenu(filter);
  };

  const selectedKey = useLocation().pathname;

  const highlight = () => {
    switch (true) {
      case selectedKey === "/":
        return ["home"];
        break;
      case selectedKey.indexOf("/home") > -1:
        return ["home"];
        break;
      case selectedKey.indexOf("/main") > -1:
        return ["home"];
        break;
      case selectedKey.indexOf("/my-learning-journey") > -1:
        return ["learningJourney"];
        break;
      case selectedKey.indexOf("/content-library") > -1:
        return ["contentLibrary"];
        break;
      case selectedKey.indexOf("/achievements") > -1:
        return ["achievements"];
        break;
      case selectedKey.indexOf("/leaderboards") > -1:
        return ["leaderboards"];
        break;
      case selectedKey.indexOf("/rewards") > -1:
        return ["rewards"];
        break;
      case selectedKey.indexOf("/team-monitoring") > -1:
        return ["teamMonitoring"];
        break;
      case selectedKey.indexOf("/reviews") > -1:
        return ["reviews"];
        break;
      case selectedKey.indexOf("/help") > -1:
        return ["help"];
        break;
      case (selectedKey.indexOf('/scan') > -1):
          return ['scan']  
        break;
      default:
        return null;
    }
  };

  useEffect(() => {
    let timer;
    if (!collapsedMenu) {
      // wait for 200ms animation collapse
      timer = setTimeout(() => setShowContent(true), 200);
    } else {
      setShowContent(false);
    }
    return () => clearTimeout(timer);
  }, [collapsedMenu]);

  return (
    <>
      <Sider
        collapsedWidth={64}
        width={215}
        className="text-center d-flex flex-column sider-menu"
        style={styles.siderStyle}
        trigger={null}
        collapsed={collapsedMenu}
        collapsible={true}
      >
        <div className="h-8">
          <Tooltip
            placement="right"
            title={
              collapsedMenu ? <span>{t("main.sidebar.show_menu")}</span> : null
            }
          >
            <FloatingHover
              name="btn-collapse-menu"
              id="arrow-collapsed"
              className={`${collapsedMenu ? "hide-menu" : "show-menu"}`}
              onClick={() => setCollapsedMenu(!collapsedMenu)}
            >
              {collapsedMenu ? (
                <RightOutlined style={styles.fontSize15} />
              ) : (
                <LeftOutlined style={styles.fontSize15} />
              )}
            </FloatingHover>
          </Tooltip>
        </div>
        <div className="menu-main-wrapper">
          <Menu
            selectedKeys={highlight()}
            mode="inline"
            inlineIndent={23}
            defaultSelectedKeys={["home"]}
            items={resultFilterMenu}
            style={styles.textAlignLeft}
          />
        </div>
        <div style={{
          position:"absolute",
          bottom:0,
          width:"100%",
          display:showContent ? "flex" : "none",
          lineHeight:"3",
          fontSize:14,
          color:"#AAAAAD",
          paddingLeft:24
        }}>
          {t("main.sidebar.version")}
        </div>
      </Sider>
    </>
  );
};

export default Sidebar;
